import { EntityStatusChip } from '../../../components/entity-detail/EntityStatusChip.js';
import { useI18nSubscription } from '../../i18n/useI18nSubscription.js';
import { i18n } from '../../../i18n/index.js';
// История взаимодействий с контактом: запись о том, что общение уже
// состоялось (звонок, письмо, личная встреча, SMS). Таблица
// contact_interactions существовала с самого начала, но записать в неё было
// нечем — на карточке показывался пустой таймлайн.
//
// Это НЕ планирование: встреча с участниками, приглашением и .ics — сущность
// CalendarEvent («Встречи»). Поэтому руками здесь заводится только прошедшее,
// а тип «Заметка» убран — для заметок есть своя сущность со своими связями.
import { useState, type ReactElement } from 'react';
import { Alert, Button, Form, Input, Modal, Select, Spin, Tooltip } from 'antd';
import { EmptyState } from '../../../components/entity-detail/EmptyState.js';
import type { ColumnsType } from 'antd/es/table/interface';
import { EditOutlined, PlusOutlined } from '@ant-design/icons';

import {
  formatDateTime,
  isoToLocalInput,
  localInputToIso,
  nowLocalInput,
} from '../../../shared/format/index.js';
import { DeleteEntityButton } from '../../../shared/ui/DeleteEntityButton.js';
import { EntityTable } from '../../../shared/ui/EntityTable.js';
import { ErrorState } from '../../../shared/ui/ErrorState.js';
import { EntityHistoryFeed } from '../../../components/entity-detail/EntityHistoryFeed.js';
import { MessageOutlined, PhoneOutlined, CalendarOutlined } from '@ant-design/icons';
import type { ContactInteractionItem, InteractionType } from '../api/contacts.client.js';
import type { NoteItem } from '../../notes/api/notes.client.js';
import {
  useContactInteractions,
  useCreateInteractionMutation,
  useDeleteInteractionMutation,
  useUpdateInteractionMutation,
} from '../hooks/useContactInteractions.js';

/** Подписи для показа: включают типы, которые больше не заводятся вручную. */
const TYPE_LABEL: Record<InteractionType, string> = {
  get call() {
    return i18n.t('crm.features.map.features.contacts.components.contactinteractionspanel.19');
  },
  get email() {
    return i18n.t('crm.features.map.features.contacts.components.contactinteractionspanel.20');
  },
  get meeting() {
    return i18n.t('crm.features.map.features.contacts.components.contactinteractionspanel.21');
  },
  get note() {
    return i18n.t('crm.features.map.features.contacts.components.contactinteractionspanel.22');
  },
  sms: 'SMS',
  get in_app() {
    return i18n.t('crm.features.map.features.contacts.components.contactinteractionspanel.23');
  },
};

/** Что можно записать вручную. «Заметка» исключена: у заметок своя сущность. */
const CREATABLE_TYPES: Array<{ value: InteractionType; label: string }> = [
  {
    value: 'call',
    get label() {
      return i18n.t('crm.features.map.features.contacts.components.contactinteractionspanel.24');
    },
  },
  {
    value: 'email',
    get label() {
      return i18n.t('crm.features.map.features.contacts.components.contactinteractionspanel.25');
    },
  },
  {
    value: 'meeting',
    get label() {
      return i18n.t('crm.features.map.features.contacts.components.contactinteractionspanel.26');
    },
  },
  { value: 'sms', label: 'SMS' },
  {
    value: 'in_app',
    get label() {
      return i18n.t('crm.features.map.features.contacts.components.contactinteractionspanel.27');
    },
  },
];

interface FormValues {
  type: InteractionType;
  subject?: string;
  body?: string;
  channel?: string;
  occurredAt?: string;
}

interface ContactInteractionsPanelProps {
  contactId: string;
  title?: string;
  compact?: boolean;
  notes?: NoteItem[];
}

export function ContactInteractionsPanel({
  contactId,
  compact = false,
  notes = [],
  title = i18n.t(
    'crm.features.features.contacts.components.contactinteractionspanel.история_взаимодействий',
  ),
}: ContactInteractionsPanelProps): ReactElement {
  useI18nSubscription();
  const { data, isLoading, isError, error, refetch } = useContactInteractions(contactId);
  const createMut = useCreateInteractionMutation(contactId);
  const updateMut = useUpdateInteractionMutation(contactId);
  const deleteMut = useDeleteInteractionMutation(contactId);
  const [formOpen, setFormOpen] = useState(false);
  // Правка и запись — одна форма: второй набор тех же полей разъезжался бы с первым.
  const [editing, setEditing] = useState<ContactInteractionItem | null>(null);
  const [form] = Form.useForm<FormValues>();

  const saveError = (createMut.error ?? updateMut.error) as Error | null;

  const interactions = data ?? [];

  const columns: ColumnsType<ContactInteractionItem> = [
    {
      title: i18n.t('crm.features.features.contacts.components.contactinteractionspanel.тип'),
      dataIndex: 'type',
      key: 'type',
      width: 130,
      render: (type: string) => {
        const known = type as InteractionType;
        return <EntityStatusChip tone="default">{TYPE_LABEL[known] ?? type}</EntityStatusChip>;
      },
    },
    {
      title: i18n.t('crm.features.features.contacts.components.contactinteractionspanel.тема'),
      key: 'subject',
      render: (_, interaction) => (
        <div className="flex flex-col">
          <span className="font-medium" style={{ color: 'var(--ant-color-text)' }}>
            {interaction.subject ?? '—'}
          </span>
          {interaction.body ? (
            <span className="text-xs" style={{ color: 'var(--ant-color-text-tertiary)' }}>
              {interaction.body}
            </span>
          ) : null}
        </div>
      ),
    },
    {
      title: i18n.t('crm.features.features.contacts.components.contactinteractionspanel.канал'),
      dataIndex: 'channel',
      key: 'channel',
      width: 140,
      render: (channel?: string | null) =>
        channel ? (
          <span className="text-sm" style={{ color: 'var(--ant-color-text-secondary)' }}>
            {channel}
          </span>
        ) : (
          <span style={{ color: 'var(--ant-color-text-tertiary)' }}>—</span>
        ),
    },
    {
      title: i18n.t('crm.features.features.contacts.components.contactinteractionspanel.когда'),
      key: 'occurredAt',
      width: 170,
      defaultSortOrder: 'descend',
      sorter: (a, b) => new Date(a.occurredAt).getTime() - new Date(b.occurredAt).getTime(),
      render: (_, interaction) => (
        <span className="text-sm" style={{ color: 'var(--ant-color-text-secondary)' }}>
          {formatDateTime(interaction.occurredAt)}
        </span>
      ),
    },
    {
      title: i18n.t('crm.features.features.contacts.components.contactinteractionspanel.действия'),
      key: 'actions',
      width: 90,
      render: (_, interaction) => (
        <div className="flex gap-1">
          <Tooltip
            title={i18n.t(
              'crm.features.features.contacts.components.contactinteractionspanel.редактировать',
            )}
          >
            <Button
              size="small"
              type="text"
              aria-label={i18n.t(
                'crm.features.features.contacts.components.contactinteractionspanel.редактировать_взаимодействие',
              )}
              icon={<EditOutlined />}
              onClick={() => {
                setEditing(interaction);
                form.setFieldsValue({
                  type: interaction.type as InteractionType,
                  subject: interaction.subject ?? '',
                  body: interaction.body ?? '',
                  channel: interaction.channel ?? '',
                  occurredAt: isoToLocalInput(interaction.occurredAt),
                });
                setFormOpen(true);
              }}
            />
          </Tooltip>
          <DeleteEntityButton
            iconOnly
            label={i18n.t(
              'crm.features.features.contacts.components.contactinteractionspanel.удалить_запись',
            )}
            title={i18n.t(
              'crm.features.features.contacts.components.contactinteractionspanel.удалить_запись_2',
            )}
            description={i18n.t(
              'crm.features.features.contacts.components.contactinteractionspanel.запись_пропадет_из_журнала_в_истории_конта',
            )}
            loading={deleteMut.isPending && deleteMut.variables === interaction.id}
            onConfirm={() => deleteMut.mutateAsync(interaction.id)}
          />
        </div>
      ),
    },
  ];

  async function handleSubmit(values: FormValues): Promise<void> {
    const occurredAt = values.occurredAt ? (localInputToIso(values.occurredAt) ?? '') : undefined;

    if (editing) {
      // В режиме правки пустое поле — это «очистить», а не «не трогать».
      await updateMut.mutateAsync({
        id: editing.id,
        input: {
          type: values.type,
          subject: values.subject?.trim() ? values.subject : null,
          body: values.body?.trim() ? values.body : null,
          channel: values.channel?.trim() ? values.channel : null,
          ...(occurredAt ? { occurredAt } : {}),
        },
      });
    } else {
      await createMut.mutateAsync({
        type: values.type,
        ...(values.subject ? { subject: values.subject } : {}),
        ...(values.body ? { body: values.body } : {}),
        ...(values.channel ? { channel: values.channel } : {}),
        ...(occurredAt ? { occurredAt } : {}),
      });
    }

    form.resetFields();
    setEditing(null);
    setFormOpen(false);
  }

  const createAction = (
    <Button
      type={compact ? 'default' : 'primary'}
      size="small"
      icon={<PlusOutlined />}
      onClick={() => {
        setEditing(null);
        form.setFieldsValue({
          type: 'call',
          subject: '',
          body: '',
          channel: '',
          occurredAt: nowLocalInput(),
        });
        setFormOpen(true);
      }}
    >
      {i18n.t('crm.features.features.contacts.components.contactinteractionspanel.записать')}
    </Button>
  );
  return (
    <div className={compact ? '' : 'px-6 py-5'}>
      <div className="mb-4 flex items-center justify-between">
        <div
          className={
            compact ? 'entity-section-title' : 'text-xs font-semibold uppercase tracking-wider'
          }
          style={{ color: 'var(--ant-color-text)' }}
        >
          {title}
        </div>
        {!compact || interactions.length > 0 || notes.length > 0 ? createAction : null}
      </div>

      {isError ? (
        <ErrorState
          title={i18n.t(
            'crm.features.features.contacts.components.contactinteractionspanel.не_удалось_загрузить_взаимодействия',
          )}
          error={error}
          onRetry={() => void refetch()}
        />
      ) : isLoading ? (
        <div className="flex justify-center py-6">
          <Spin />
        </div>
      ) : compact ? (
        <EntityHistoryFeed
          emptyAction={createAction}
          items={[
            ...interactions.map((interaction) => ({
              id: interaction.id,
              date: interaction.occurredAt,
              meta: [interaction.channel, interaction.subject].filter(Boolean).join(' · '),
              icon:
                interaction.type === 'call' ? (
                  <PhoneOutlined />
                ) : interaction.type === 'meeting' ? (
                  <CalendarOutlined />
                ) : (
                  <MessageOutlined />
                ),
              text: (
                <>
                  {TYPE_LABEL[interaction.type as InteractionType] ?? interaction.type} ·{' '}
                  {interaction.body}
                </>
              ),
              actions: [
                {
                  key: 'edit',
                  label: i18n.t('crm.entity_detail.edit'),
                  onClick: () => {
                    setEditing(interaction);
                    form.setFieldsValue({
                      type: interaction.type as InteractionType,
                      subject: interaction.subject ?? '',
                      body: interaction.body ?? '',
                      channel: interaction.channel ?? '',
                      occurredAt: isoToLocalInput(interaction.occurredAt),
                    });
                    setFormOpen(true);
                  },
                },
                {
                  key: 'delete',
                  label: i18n.t('crm.entity_detail.delete'),
                  danger: true,
                  disabled: deleteMut.isPending,
                  confirm: {
                    title: i18n.t('crm.entity_detail.delete_confirm'),
                    description: i18n.t(
                      'crm.features.features.contacts.components.contactinteractionspanel.запись_пропадет_из_журнала_в_истории_конта',
                    ),
                  },
                  onClick: () => deleteMut.mutateAsync(interaction.id),
                },
              ],
            })),
            ...notes.map((note) => ({
              id: `note:${note.id}`,
              date: note.createdAt,
              meta: note.createdByMember?.user.name ?? '',
              icon: <MessageOutlined />,
              text: (
                <>
                  <strong>{TYPE_LABEL.note}</strong> · {note.body}
                </>
              ),
            })),
          ]}
        />
      ) : interactions.length === 0 ? (
        <EmptyState
          text={i18n.t(
            'crm.features.features.contacts.components.contactinteractionspanel.пока_ничего_не_записано',
          )}
        />
      ) : (
        <EntityTable<ContactInteractionItem>
          dataSource={interactions}
          columns={columns}
          paginated={false}
        />
      )}

      <Modal
        open={formOpen}
        title={
          editing
            ? i18n.t(
                'crm.features.features.contacts.components.contactinteractionspanel.редактировать_взаимодействие_2',
              )
            : i18n.t(
                'crm.features.features.contacts.components.contactinteractionspanel.записать_состоявшееся_взаимодействие',
              )
        }
        okText={i18n.t(
          'crm.features.features.contacts.components.contactinteractionspanel.сохранить',
        )}
        cancelText={i18n.t(
          'crm.features.features.contacts.components.contactinteractionspanel.отмена',
        )}
        confirmLoading={createMut.isPending || updateMut.isPending}
        onOk={() => form.submit()}
        onCancel={() => {
          setEditing(null);
          setFormOpen(false);
        }}
        destroyOnHidden
      >
        {saveError ? (
          <Alert
            type="error"
            showIcon
            style={{ marginBottom: 16 }}
            title={i18n.t(
              'crm.features.features.contacts.components.contactinteractionspanel.не_удалось_сохранить',
            )}
            description={saveError.message}
          />
        ) : null}
        <Form<FormValues>
          form={form}
          layout="vertical"
          onFinish={(values) => void handleSubmit(values)}
        >
          <Form.Item
            name="type"
            label={i18n.t(
              'crm.features.features.contacts.components.contactinteractionspanel.тип_2',
            )}
            rules={[
              {
                required: true,
                message: i18n.t(
                  'crm.features.features.contacts.components.contactinteractionspanel.выберите_тип',
                ),
              },
            ]}
            extra={i18n.t(
              'crm.features.features.contacts.components.contactinteractionspanel.фиксируется_уже_состоявшееся_общение_назна',
            )}
          >
            <Select options={CREATABLE_TYPES} />
          </Form.Item>
          <Form.Item
            name="subject"
            label={i18n.t(
              'crm.features.features.contacts.components.contactinteractionspanel.тема_2',
            )}
          >
            <Input
              maxLength={200}
              placeholder={i18n.t(
                'crm.features.features.contacts.components.contactinteractionspanel.обсудили_условия',
              )}
            />
          </Form.Item>
          <Form.Item
            name="body"
            label={i18n.t(
              'crm.features.features.contacts.components.contactinteractionspanel.детали',
            )}
          >
            <Input.TextArea rows={3} maxLength={5000} />
          </Form.Item>
          <Form.Item
            name="channel"
            label={i18n.t(
              'crm.features.features.contacts.components.contactinteractionspanel.канал_2',
            )}
          >
            <Input
              maxLength={50}
              placeholder={i18n.t(
                'crm.features.features.contacts.components.contactinteractionspanel.телефон_whatsapp_почта',
              )}
            />
          </Form.Item>
          <Form.Item
            name="occurredAt"
            label={i18n.t(
              'crm.features.features.contacts.components.contactinteractionspanel.когда_2',
            )}
          >
            {/* Взаимодействие фиксируется задним числом, поэтому прошлое разрешено. */}
            <input type="datetime-local" className="entity-native-input" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
